/**
 * Cancellation Guide Service
 *
 * Microservice for managing subscription cancellation guides
 * Provides step-by-step cancellation instructions for popular services
 * Handles user requests for guides that don't exist yet
 */

import { supabase, getCurrentUserId } from './supabaseClient';

/**
 * Service name aliases - maps common variations to the canonical name in the database
 * This handles cases where the email classifier or user input uses different naming
 */
const SERVICE_ALIASES = {
  // Claude/Anthropic variations
  'Anthropic': 'Claude',
  'Anthropic Claude': 'Claude',
  'Claude AI': 'Claude',
  'anthropic': 'Claude',

  // Uber variations
  'Uber': 'Uber One',
  'uber': 'Uber One',

  // Add more aliases as needed
};

/**
 * Normalize service name using aliases
 * @param {string} serviceName - Original service name
 * @returns {string} Normalized service name
 */
const normalizeServiceName = (serviceName) => {
  if (!serviceName) return serviceName;

  // Check if there's an alias for this service name
  const normalized = SERVICE_ALIASES[serviceName] || SERVICE_ALIASES[serviceName.trim()];

  if (normalized) {
    console.log(`📝 Service name alias: "${serviceName}" → "${normalized}"`);
    return normalized;
  }

  return serviceName;
};

/**
 * Get cancellation guide for a specific service
 * @param {string} serviceName - Name of the service (e.g., "Netflix", "Spotify")
 * @returns {Object|null} Cancellation guide object or null if not found
 */
export const getCancellationGuide = async (serviceName) => {
  try {
    console.log('🔍 getCancellationGuide called with:', {
      serviceName,
      type: typeof serviceName,
      length: serviceName?.length,
      trimmed: serviceName?.trim(),
    });

    // Normalize service name using aliases
    const normalizedName = normalizeServiceName(serviceName);

    const { data, error } = await supabase
      .from('cancellation_guides')
      .select('*')
      .ilike('service_name', normalizedName)
      .single();

    console.log('📊 Supabase response:', { data, error });

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - guide doesn't exist
        console.log('❌ No guide found for:', normalizedName);
        return null;
      }
      throw error;
    }

    console.log('✅ Guide found!', data);
    return data;
  } catch (error) {
    console.error('Error fetching cancellation guide:', error);
    return null;
  }
};

/**
 * Get all available cancellation guides
 * @returns {Array} Array of all cancellation guides
 */
export const getAllCancellationGuides = async () => {
  try {
    const { data, error } = await supabase
      .from('cancellation_guides')
      .select('*')
      .order('service_name', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching all cancellation guides:', error);
    return [];
  }
};

/**
 * Get generic cancellation guide for services without specific guides
 * @returns {Object} Generic guide with common cancellation steps
 */
export const getGenericGuide = () => {
  return {
    service_name: 'Generic',
    cancellation_url: null,
    steps: [
      'Sign in to your account on the service\'s website',
      'Navigate to Account Settings or Profile Settings',
      'Look for "Subscription", "Billing", or "Membership" section',
      'Find the "Cancel Subscription" or "Cancel Membership" option',
      'Follow the prompts to confirm cancellation',
      'Check your email for cancellation confirmation',
      'Note: Some services may require contacting customer support'
    ],
    estimated_time: '5-10 minutes',
    difficulty: 'medium'
  };
};

/**
 * Request a cancellation guide for a service that doesn't have one yet
 * Creates an upvote/request for admins to prioritize creating the guide
 * @param {string} serviceName - Name of the service
 * @param {string} subscriptionId - ID of the subscription
 * @returns {boolean} Success status
 */
export const requestCancellationGuide = async (serviceName, subscriptionId = null) => {
  try {
    const userId = await getCurrentUserId();

    // Check if user already requested this guide
    const existingRequest = await checkIfUserVoted(userId, serviceName);
    if (existingRequest) {
      console.log('User has already requested this guide');
      return false;
    }

    const { error } = await supabase
      .from('cancellation_guide_requests')
      .insert({
        user_id: userId,
        service_name: serviceName,
        subscription_id: subscriptionId
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error requesting cancellation guide:', error);
    return false;
  }
};

/**
 * Check if user has already voted/requested a guide for a service
 * @param {string} userId - User ID
 * @param {string} serviceName - Name of the service
 * @returns {boolean} True if user has already voted
 */
export const checkIfUserVoted = async (userId, serviceName) => {
  try {
    const { data, error } = await supabase
      .from('cancellation_guide_requests')
      .select('id')
      .eq('user_id', userId)
      .ilike('service_name', serviceName)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return !!data;
  } catch (error) {
    console.error('Error checking user vote:', error);
    return false;
  }
};

/**
 * Get vote count for a specific service
 * @param {string} serviceName - Name of the service
 * @returns {number} Number of votes/requests
 */
export const getVoteCount = async (serviceName) => {
  try {
    const { data, error } = await supabase
      .from('cancellation_guide_request_counts')
      .select('vote_count')
      .ilike('service_name', serviceName)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data ? data.vote_count : 0;
  } catch (error) {
    console.error('Error fetching vote count:', error);
    return 0;
  }
};

/**
 * Get all service requests ordered by vote count
 * @returns {Array} Array of service requests with vote counts
 */
export const getAllServiceRequests = async () => {
  try {
    const { data, error } = await supabase
      .from('cancellation_guide_request_counts')
      .select('*');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching service requests:', error);
    return [];
  }
};

/**
 * Remove a user's vote/request for a guide
 * @param {string} serviceName - Name of the service
 * @returns {boolean} Success status
 */
export const removeGuideRequest = async (serviceName) => {
  try {
    const userId = await getCurrentUserId();

    const { error } = await supabase
      .from('cancellation_guide_requests')
      .delete()
      .eq('user_id', userId)
      .ilike('service_name', serviceName);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing guide request:', error);
    return false;
  }
};

export default {
  getCancellationGuide,
  getAllCancellationGuides,
  getGenericGuide,
  requestCancellationGuide,
  checkIfUserVoted,
  getVoteCount,
  getAllServiceRequests,
  removeGuideRequest
};
